/**
 * Chrome (and other) browser Translate mutates text nodes / wraps them in <font>.
 * React then throws NotFoundError on removeChild / insertBefore during reconcile.
 * Soften those mismatches so the UI stays usable under auto-translate.
 *
 * @see https://github.com/facebook/react/issues/11538
 */
export function patchDomForBrowserTranslate(): void {
  if (typeof Node !== "function" || !Node.prototype) return;

  const proto = Node.prototype;

  if ((proto as { __legalixTranslatePatch?: boolean }).__legalixTranslatePatch) {
    return;
  }
  (proto as { __legalixTranslatePatch?: boolean }).__legalixTranslatePatch = true;

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      return child;
    }
    try {
      return originalRemoveChild.call(this, child) as T;
    } catch (error) {
      if (isDomNotFoundError(error)) return child;
      throw error;
    }
  };

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    try {
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    } catch (error) {
      if (isDomNotFoundError(error)) return newNode;
      throw error;
    }
  };
}

function isDomNotFoundError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === "NotFoundError" || error.code === 8)
  );
}
